import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { WhenReady } from '../common/WhenReady';
import { FetchSiteState } from '../common/FetchSiteState';
import { SiteStyle } from '../common/SiteStyle';
import { Reporting } from '../common/Reporting';
import { Spacer } from '../common/Spacer';
import { SiteHeader } from '../common/SiteHeader';
import { useCheckinState, useReportState, useSiteState } from '../../state/state';
import { DetailStep } from './DetailStep';
import { RulesStep } from './RulesStep';
import { ContactsStep } from './ContactsStep';
import { ActivityStep } from './ActivityStep';
import { Logo } from '../common/Logo';
import { CheckinForm, chooseNextStep, isLastPage, Step } from '../../service/checkin-flow';
import { Activity, ContactData, Token } from '../../model/Model';
import { DeclarationStep } from './DeclarationStep';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { getActivities, getContacts } from '../../service/rest';

export const CheckinFlow = () => {
    const { token } = useParams<Token>();
    const history = useHistory();

    const { settings } = useSiteState();
    const { reportError } = useReportState();
    const { loading: loadingCheckin, activeCheckin, checkIn } = useCheckinState();

    const [step, setStep] = useState(Step.DETAILS);
    const [checkinForm, setCheckinForm] = useState<CheckinForm>();

    const [buttonLoading, setButtonLoading] = useState(false);
    const [buttonText, setButtonText] = useState('Next');
    const [buttonLoadingText, setButtonLoadingText] = useState('Processing...');
    const [contacts, setContacts] = useState<ContactData[]>();
    const [activities, setActivities] = useState<Activity[]>();

    const loading = useMemo(() => loadingCheckin || buttonLoading, [loadingCheckin, buttonLoading]);

    const computeNextStep = useCallback((form?: CheckinForm) => {
        const formToUse = form ? form : checkinForm;
        if (settings) {
            const nextStep = chooseNextStep(settings, step);
            switch (nextStep) {
                case Step.CONTACT:
                    setButtonLoading(true);
                    getContacts(token).then(contacts => {
                        setContacts(contacts);
                        setStep(nextStep);
                    }).catch(() => {
                        reportError('Unable to fetch site contacts, please try again later');
                    }).finally(() => {
                        setButtonLoading(false);
                    });
                    return true;
                case Step.ACTIVITY:
                    setButtonLoading(true);
                    getActivities(token, settings?.siteActivitiesType).then(activities => {
                        setActivities(activities);
                        setStep(nextStep);
                    }).catch(() => {
                        reportError('Unable to fetch site activities, please try again later');
                    }).finally(() => {
                        setButtonLoading(false);
                    });
                    return true;
            }
            if (nextStep !== Step.DONE) {
                setStep(nextStep);
                return true;
            }
        }
        performCheckin(formToUse);
        return false;
    }, [settings, step, checkinForm]);

    const performCheckin = useCallback((form?: CheckinForm) => {
        if (settings && form) {
            checkIn(token, form, (success: boolean) => {
                if (success) {
                    history.replace(`/checked/${token}`);
                } else {
                    history.replace(`/fail/${token}`)
                }
            });
        }
    }, [settings, token]);

    const onDetailsComplete = (form: CheckinForm) => {
        setCheckinForm(form);
        computeNextStep(form);
    };

    const onRulesComplete = () => {
        computeNextStep();
    };

    const onActivityComplete = (form: CheckinForm) => {
        if (checkinForm) {
            const updatedForm = {
                ...checkinForm,
                activity: form.activity,
            };
            setCheckinForm(updatedForm);
            computeNextStep(updatedForm);
        }
    };

    const onContactsComplete = (form: CheckinForm) => {
        if (checkinForm) {
            const updatedForm = {
                ...checkinForm,
                contact: form.contact,
            };
            setCheckinForm(updatedForm);
            computeNextStep(updatedForm);
        }
    };

    const onDeclarationComplete = (form: CheckinForm) => {
        if (checkinForm) {
            const updatedForm = {
                ...checkinForm,
                declaration: form.declaration,
            };
            setCheckinForm(updatedForm);
            computeNextStep(updatedForm);
        }
    };

    useEffect(() => {
        if (settings && step) {
            if (isLastPage(settings, step)) {
                setButtonText('Check In');
                setButtonLoadingText('Checking In...');
            } else {
                setButtonText('Next');
                setButtonLoadingText('Processing...');
            }
        }
    }, [step, settings]);

    useEffect(() => {
        if (step && activeCheckin) {
            if (step === Step.DONE) {
            }
        }
    }, [step, activeCheckin]);

    return (
        <>
            <FetchSiteState />
            <SiteStyle />
            <Reporting />
            <WhenReady>
                <Spacer height='2em' />
                <SiteHeader />
                <Spacer height='2em' />
                {step == Step.DETAILS &&
                <DetailStep onStepComplete={onDetailsComplete}
                            buttonText={buttonText}
                            buttonLoadingText={buttonLoadingText}
                            buttonLoading={loading} />}
                {step == Step.RULES &&
                <RulesStep onStepComplete={onRulesComplete}
                           buttonText={buttonText}
                           buttonLoadingText={buttonLoadingText}
                           buttonLoading={loading} />}
                {step == Step.CONTACT &&
                <ContactsStep onStepComplete={onContactsComplete}
                              buttonText={buttonText}
                              buttonLoadingText={buttonLoadingText}
                              buttonLoading={loading}
                              contacts={contacts} />}
                {step == Step.ACTIVITY &&
                <ActivityStep onStepComplete={onActivityComplete}
                              buttonText={buttonText}
                              buttonLoadingText={buttonLoadingText}
                              buttonLoading={loading}
                              activities={activities} />}
                {(step == Step.DECLARATION) && checkinForm &&
                <DeclarationStep onStepComplete={onDeclarationComplete}
                                 buttonText={buttonText}
                                 buttonLoadingText={buttonLoadingText}
                                 buttonLoading={loading} />}
                <Spacer height='6em' />
                <Logo />
            </WhenReady>
        </>
    )
}
